import {SERVICE_NAMES} from '../constants';
import {createApiCall} from '../utils';
import type {ServiceName} from '../constants';
import type {
    ApiCallOptions,
    PaginatedResponse,
    ArchiveRequestResource,
    ArchiveRequestTransactionResource,
    ArchiveRequestListParams,
    ArchiveRequestTransactionListParams,
    StoreArchiveRequestRequest
} from './api.d';

/** Archive Service API client — five routes; `check-overlap`/`preview`/`retry` are gone, overlap is now a download-time choice. */

const createArchiveApiCall = <T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string
) => createApiCall<T>(SERVICE_NAMES.ARCHIVE as ServiceName, method, endpoint);

export const archiveApi = {
    /** GET /archive-requests — supports `status`, `page` and `per_page` (max 100) */
    getArchiveRequests: ({params, ...options}: {params?: ArchiveRequestListParams} & ApiCallOptions = {}) => (
        createArchiveApiCall<PaginatedResponse<ArchiveRequestResource>>('GET', 'archive-requests')({...options, params})
    ),

    /** GET /archive-requests/{archiveRequest} — the request only; its transactions live on their own endpoint */
    getArchiveRequest: ({archiveRequestId, ...options}: {archiveRequestId: string} & ApiCallOptions) => (
        createArchiveApiCall<ArchiveRequestResource>('GET', `archive-requests/${archiveRequestId}`)({...options})
    ),

    /** GET .../transactions — paginated, failed-first; `transaction_status` goes out as `transaction_status[]`, what it validates. */
    getArchiveRequestTransactions: ({archiveRequestId, params, ...options}: {
        archiveRequestId: string;
        params?: ArchiveRequestTransactionListParams;
    } & ApiCallOptions) => (
        createArchiveApiCall<PaginatedResponse<ArchiveRequestTransactionResource>>('GET', `archive-requests/${archiveRequestId}/transactions`)({...options, params})
    ),

    /** POST /archive-requests — answers 201; 422 on a bad body */
    createArchiveRequest: ({data, ...options}: {data: StoreArchiveRequestRequest} & ApiCallOptions) => (
        createArchiveApiCall<ArchiveRequestResource>('POST', 'archive-requests')({...options, data})
    ),

    /** DELETE /archive-requests/{archiveRequest} — 204, and 404 for an unknown id */
    deleteArchiveRequest: ({archiveRequestId, ...options}: {archiveRequestId: string} & ApiCallOptions) => (
        createArchiveApiCall<void>('DELETE', `archive-requests/${archiveRequestId}`)({...options})
    )
};
